import type { AddCreditCardDTO, CreditCardDTO } from 'store/accounts/dtos';
import { toAddCreditCardModel, toCreditCardModel } from 'store/accounts/helpers';
import type { AddCreditCardModel, CreditCardModel } from 'store/accounts/models';
import type { AppEndpointBuilder } from 'store/types';
import type { Paginate } from 'types';

import {
  toOrganizationDetailsModel,
  toOrganizationEntryModel,
  toOrganizationInvitationModel,
  toOrganizationMemberModel,
} from './helpers';
import type {
  OrganizationDetailsModel,
  OrganizationEntryModel,
  OrganizationInvitationModel,
  OrganizationMemberModel,
} from './models';
import type {
  AddOrganizationMemberPayload,
  CreateOrganizationPayload,
  DeleteOrganizationMemberPayload,
  GetOrganizationMembersPayload,
  SetActiveOrganizationPayload,
  UpdateOrganizationMemberRolePayload,
  UpdateOrganizationPayload,
} from './payloads';

export const organizationEndpoints = (builder: AppEndpointBuilder) => ({
  // #region Queries
  getOrganizationCreditCard: builder.query<CreditCardModel | null, void>({
    query: () => ({
      url: 'organisation/billing/card-details',
      method: 'GET',
    }),
    providesTags: ['Organization-CreditCard'],
    transformResponse: (data: CreditCardDTO | undefined) => (data ? toCreditCardModel(data) : null),
  }),

  getOrganizations: builder.query<OrganizationEntryModel[], void>({
    query: () => ({
      url: 'organisations',
      method: 'GET',
    }),
    providesTags: ['Organization'],
    transformResponse: (data: OrganizationEntryModel[]) => data.map(toOrganizationEntryModel),
  }),

  getOrganizationDetails: builder.query<OrganizationDetailsModel, void>({
    query: () => ({
      url: 'organisation/details',
      method: 'GET',
    }),
    providesTags: ['Organization'],
    transformResponse: toOrganizationDetailsModel,
  }),

  getOrganizationMembers: builder.query<Paginate<OrganizationMemberModel>, GetOrganizationMembersPayload>({
    query: ({ page }) => ({
      url: 'organisation/members',
      method: 'GET',
      params: { page },
    }),
    providesTags: ['Organization-Member'],
    transformResponse: toOrganizationMemberModel,
  }),
  // #endregion Queries

  // #region Mutations
  addOrganizationCreditCard: builder.mutation<AddCreditCardModel, void>({
    query: () => ({
      url: 'organisation/billing/card/authorize',
      method: 'POST',
    }),
    invalidatesTags: ['Organization-CreditCard', 'Session'],
    transformResponse: (data: AddCreditCardDTO) => toAddCreditCardModel(data),
  }),

  deleteOrganizationCreditCard: builder.mutation<void, void>({
    query: () => ({
      url: 'organisation/billing/card/unauthorize',
      method: 'POST',
    }),
    invalidatesTags: ['Organization-CreditCard', 'Session'],
  }),

  updateOrganization: builder.mutation<OrganizationDetailsModel, UpdateOrganizationPayload>({
    query: (data) => ({
      url: 'organisation/details',
      method: 'PATCH',
      data,
    }),
    invalidatesTags: ['Organization'],
    transformResponse: toOrganizationDetailsModel,
  }),

  createOrganization: builder.mutation<OrganizationEntryModel, CreateOrganizationPayload>({
    query: (data) => ({
      url: 'organisations',
      method: 'POST',
      data,
    }),
    invalidatesTags: ['Organization'],
    transformResponse: toOrganizationEntryModel,
  }),

  addOrganizationMember: builder.mutation<OrganizationInvitationModel, AddOrganizationMemberPayload>({
    query: (data) => ({
      url: 'organisation/invitations',
      method: 'POST',
      data,
    }),
    invalidatesTags: ['Organization-Member'],
    transformResponse: toOrganizationInvitationModel,
  }),

  updateOrganizationMemberRole: builder.mutation<OrganizationMemberModel, UpdateOrganizationMemberRolePayload>({
    query: ({ id, ...data }) => ({
      url: `organisation/members/${id}`,
      method: 'PATCH',
      data,
    }),
    invalidatesTags: ['Organization-Member'],
    transformResponse: toOrganizationMemberModel,
  }),

  deleteOrganizationMember: builder.mutation<void, DeleteOrganizationMemberPayload>({
    query: ({ id }) => ({
      url: `organisation/members/${id}`,
      method: 'DELETE',
    }),
    invalidatesTags: ['Organization-Member'],
  }),

  setActiveOrganization: builder.mutation<OrganizationEntryModel | void, SetActiveOrganizationPayload>({
    query: (data) => ({
      url: 'organisations/active',
      method: 'PUT',
      data,
    }),
    invalidatesTags: ['Organization', 'Organization-Member'],
  }),
  // #endregion Mutations
});
